<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.showProduct") || "Show Product" }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #e1423d">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form>
        <div class="row">
          <!-- Start:: Main Image Display -->
          <div class="col-12 text-center my-3">
            <div class="preview-container">
              <img
                v-if="data.main_image.path"
                :src="data.main_image.path"
                alt="Product Main Image"
                style="
                  max-width: 300px;
                  max-height: 300px;
                  border: 1px solid #ccc;
                  border-radius: 8px;
                "
              />
            </div>
          </div>
          <!-- End:: Main Image Display -->

          <!-- Start:: Additional Images Display -->
          <div class="col-12 my-3" v-if="additionalImagesUrls.length > 0">
            <h5>
              {{ $t("PLACEHOLDERS.additionalImages") || "Additional Images" }}
            </h5>
            <div class="image-gallery d-flex flex-wrap gap-3">
              <div
                v-for="(imgUrl, index) in additionalImagesUrls"
                :key="index"
                class="image-preview-item"
              >
                <img
                  :src="imgUrl"
                  alt="Additional Image"
                  style="
                    max-width: 150px;
                    max-height: 150px;
                    border: 1px solid #ccc;
                    border-radius: 8px;
                  "
                />
              </div>
            </div>
          </div>
          <!-- End:: Additional Images Display -->

          <!-- Start:: Name Input (Arabic) -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.codename')"
            v-model.trim="data.name"
            disabled
          />
          <!-- End:: Name Input (Arabic) -->

          <!-- Start:: Name Input (English) -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.codenameEn')"
            v-model.trim="data.nameEn"
            disabled
          />
          <!-- End:: Name Input (English) -->

          <!-- Start:: Description Input (Arabic) -->
          <base-input
            col="6"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.descProd')"
            v-model.trim="data.descProd"
            disabled
          />
          <!-- End:: Description Input (Arabic) -->

          <!-- Start:: Description Input (English) -->
          <base-input
            col="6"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.descProdEn')"
            v-model.trim="data.descProdEn"
            disabled
          />
          <!-- End:: Description Input (English) -->

          <!-- Start:: Category Display -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.category') || 'Product Category'"
            :value="categoryName"
            disabled
          />
          <!-- End:: Category Display -->

          <!-- Start:: Weight or Size Display -->
          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.weightOrSize') || 'Weight or Size'"
            v-model.number="data.weight_or_size"
            disabled
          />
          <!-- End:: Weight or Size Display -->

          <!-- Start:: Weight Unit Display -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.weightUnit') || 'Weight Unit'"
            :value="weightUnitName"
            disabled
          />
          <!-- End:: Weight Unit Display -->

          <base-input
            v-if="data.frozen_status"
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.frozen_status') || 'Freeze Status'"
            :value="
              data.frozen_status == 'frozen'
                ? $t('PLACEHOLDERS.frozen')
                : $t('PLACEHOLDERS.chilled')
            "
            disabled
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.productCode') || 'Product Code'"
            :value="data.product_code"
            disabled
          />

          <!-- Packaging & Inventory (General Display) -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.packagingUnit') || 'Packaging Unit'"
            :value="getPackagingUnitName(data.packaging_unit)"
            disabled
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.itemUnit') || 'Item Unit'"
            :value="getItemUnitName(data.item_unit)"
            disabled
          />

          <base-input
            col="6"
            type="number"
            :placeholder="
              $t('PLACEHOLDERS.availableUnits') || 'Available Units'
            "
            :value="data.available_units"
            disabled
          />

          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.itemsPerUnit') || 'Items Per Unit'"
            :value="data.items_per_unit"
            disabled
          />

          <base-input
            col="6"
            type="number"
            :placeholder="
              $t('PLACEHOLDERS.lowStockThreshold') || 'Low Stock Threshold'
            "
            :value="data.low_stock_threshold"
            disabled
          />
          <!-- End:: Packaging & Inventory -->

          <!-- Start:: Prices Section -->
          <div class="col-12">
            <div class="prices-section-header mb-4">
              <h5 class="mb-0" style="color: #e1423d; font-weight: 600">
                <i class="fas fa-dollar-sign mr-2"></i>
                {{
                  $t("PLACEHOLDERS.pricesByQuantity") || "Prices by Quantity"
                }}
              </h5>
            </div>
            <div
              v-for="(price, index) in data.prices"
              :key="index"
              class="price-card mb-3"
            >
              <div class="price-card-header">
                <span class="price-card-title">
                  {{ $t("PLACEHOLDERS.priceLevel") || "Price Level" }} #{{
                    index + 1
                  }}
                </span>
              </div>
              <div class="price-card-body">
                <div class="row">
                  <div class="col-md-6 col-lg-4">
                    <base-input
                      col="12"
                      type="number"
                      :placeholder="$t('PLACEHOLDERS.riyal_price') || 'Price'"
                      :value="price.price"
                      disabled
                    />
                  </div>
                  <div class="col-md-6 col-lg-4">
                    <base-input
                      col="12"
                      type="number"
                      :placeholder="
                        $t('PLACEHOLDERS.minimumQuantity') || 'Minimum Quantity'
                      "
                      :value="price.minimum_quantity"
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- End:: Prices Section -->

          <!-- Start:: Product Status Display -->
          <div class="input_wrapper switch_wrapper my-5 col-6">
            <label
              class="form-label"
              style="margin: 0 13px !important; color: #e1423d"
            >
              {{ $t("PLACEHOLDERS.productStatus") || "Product Status" }}:
            </label>
            <v-switch
              color="green"
              :label="
                data.is_active
                  ? $t('PLACEHOLDERS.active')
                  : $t('PLACEHOLDERS.notActive')
              "
              v-model="data.is_active"
              hide-details
              disabled
            ></v-switch>
          </div>
          <!-- End:: Product Status Display -->

          <!-- Start:: Created At Display -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('TABLES.Workplaces.date') || 'Created At'"
            :value="data.created_at"
            disabled
          />
          <!-- End:: Created At Display -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ShowProduct",

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Display
      data: {
        name: null,
        nameEn: null,
        descProd: null,
        descProdEn: null,
        main_image: {
          path: null,
        },
        category_id: null,
        categoryName: null,
        frozen_status: false,
        product_code: null,
        weight_or_size: null,
        weight_unit: null,
        prices: [],
        is_active: true,
        created_at: null,
      },
      // End:: Data Collection To Display

      additionalImagesUrls: [],
    };
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
    categoryName() {
      return this.data.categoryName || "-";
    },
    weightUnitName() {
      const units = {
        Bag: "كيس - Bag",
        Block: "بلوك - Block",
        Box: "كرتون - Box",
        Gallon: "جالون - Gallon",
        kg: this.$t("PLACEHOLDERS.kg") || "kg",
        Ltr: "لتر - Ltr",
        Pail: "سطل - Pail",
        Pc: "حبه - Pc",
        PKT: "باكيت - PKT",
        Tin: "تنك - Tin",
        punch: "ربطه - punch",
        Tray: "طبق - Tray",
        Plate: "صحن - Plate",
        g: this.$t("PLACEHOLDERS.g") || "g",
        liter: this.$t("PLACEHOLDERS.liter") || "liter",
        ml: this.$t("PLACEHOLDERS.ml") || "ml",
        carton: this.$t("PLACEHOLDERS.carton") || "carton",
        package: this.$t("PLACEHOLDERS.package") || "package",
      };
      return units[this.data.weight_unit] || this.data.weight_unit || "-";
    },
  },

  methods: {
    getPackagingUnitName(unit) {
      const units = {
        pack_bag: this.$t("PLACEHOLDERS.packBag") || "Pack Bag",
        carton: this.$t("PLACEHOLDERS.carton") || "Carton",
        gallon: this.$t("PLACEHOLDERS.gallon") || "Gallon",
        bucket: this.$t("PLACEHOLDERS.bucket") || "Bucket",
        tank: this.$t("PLACEHOLDERS.tank") || "Tank",
        packet: this.$t("PLACEHOLDERS.packet") || "Packet",
        bundle: this.$t("PLACEHOLDERS.bundle") || "Bundle",
        tray: this.$t("PLACEHOLDERS.tray") || "Tray",
      };
      return units[unit] || unit || "-";
    },

    getItemUnitName(unit) {
      const units = {
        bag: this.$t("PLACEHOLDERS.bag") || "Bag",
        kg: this.$t("PLACEHOLDERS.kg") || "Kilogram",
        liter: this.$t("PLACEHOLDERS.liter") || "Liter",
        piece: this.$t("PLACEHOLDERS.piece") || "Piece",
        plate: this.$t("PLACEHOLDERS.plate") || "Plate",
        drzn: this.$t("PLACEHOLDERS.drzn") || "Dozen",
        Bag: "كيس - Bag",
        Block: "بلوك - Block",
        Box: "كرتون - Box",
        Gallon: "جالون - Gallon",
        Ltr: "لتر - Ltr",
        Pail: "سطل - Pail",
        Pc: "حبه - Pc",
        PKT: "باكيت - PKT",
        Tin: "تنك - Tin",
        punch: "ربطه - punch",
        Tray: "طبق - Tray",
        Plate: "صحن - Plate",
      };
      return units[unit] || unit || "-";
    },

    // Start:: Show Product Data
    async showProduct() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `products/${this.$route.params.id}?include=category,translations,prices`,
        });
        const product = res.data.data;

        // Set translations
        if (product.translations) {
          this.data.name = product.translations.ar?.name || product.name;
          this.data.nameEn = product.translations.en?.name || product.name;
          this.data.descProd =
            product.translations.ar?.description || product.description;
          this.data.descProdEn =
            product.translations.en?.description || product.description;
        } else {
          this.data.name = product.name_ar || product.name;
          this.data.nameEn = product.name_en || product.name;
          this.data.descProd = product.description_ar || product.description;
          this.data.descProdEn = product.description_en || product.description;
        }

        // Set main image
        if (product.main_image_url) {
          this.data.main_image.path = product.main_image_url;
        }

        // Set additional images
        if (product.images && product.images.length > 0) {
          this.additionalImagesUrls = product.images.map(
            (img) => img.url || img
          );
        }

        // Set category
        if (product.category_id) {
          this.data.category_id = product.category_id;
        }
        if (product.category) {
          this.data.categoryName =
            product.category.name ||
            product.category.translations?.ar?.name ||
            product.category.translations?.en?.name;
        }

        // Set freeze status
        this.data.frozen_status = product?.frozen_status || null;
        this.data.product_code = product?.product_code || null;

        // Set weight and unit
        this.data.weight_or_size = product.weight_or_size || null;
        this.data.weight_unit = product.weight_unit || null;

        // Product-level packaging & inventory (source of truth)
        this.data.packaging_unit =
          product.packaging_unit ||
          (product.prices &&
            product.prices[0] &&
            (product.prices[0].packaging_unit ||
              product.prices[0].quantity_unit)) ||
          null;
        this.data.item_unit =
          product.item_unit ||
          (product.prices &&
            product.prices[0] &&
            (product.prices[0].item_unit || product.prices[0].quantity_unit)) ||
          null;
        this.data.available_units =
          product.available_units ??
          product.prices?.[0]?.available_units ??
          product.prices?.[0]?.available_quantity ??
          null;
        this.data.items_per_unit =
          product.items_per_unit ?? product.prices?.[0]?.items_per_unit ?? null;
        this.data.low_stock_threshold =
          product.low_stock_threshold ??
          product.prices?.[0]?.low_stock_threshold ??
          null;

        // Prices: only price-related fields are relevant for tiers UI
        if (product.prices && product.prices.length > 0) {
          this.data.prices = product.prices.map((price) => ({
            quantity: price.quantity,
            price: parseFloat(price.price),
            minimum_quantity: price.minimum_quantity,
          }));
        }

        // Set active status
        this.data.is_active =
          product.is_active !== undefined ? product.is_active : true;

        // Set created at
        this.data.created_at = product.created_at || null;
      } catch (error) {
        this.isWaitingRequest = false;
        console.log(error.response?.data?.message);
        this.$message.error(
          error.response?.data?.message || "Failed to load product data"
        );
      }
    },
    // End:: Show Product Data
  },

  created() {
    // Start:: Fire Methods
    this.showProduct();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.preview-container img,
.preview-container video {
  max-width: 100%;
  max-height: 200px;
  border: 1px solid #e1423d;
}

.image-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.image-preview-item {
  position: relative;
}

.prices-section-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #e1423d;
}

.price-card {
  /* background: #ffffff; */
  border: 1px solid #e1423d;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  overflow: hidden;
}

.price-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #e1423d;
}

.price-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  /* background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%); */
  border-bottom: 1px solid #e1423d;
}

.price-card-title {
  display: flex;
  align-items: center;
  /* color: #333; */
  font-size: 1rem;
}

.price-card-body {
  padding: 1.25rem;
}

.price-field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #555;
  margin-bottom: 0.5rem;
}
</style>
